import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { authService } from '@services/api'
import { useAuthStore } from '@store'
import { ROUTES } from '@constants'
import toast from 'react-hot-toast'
import { TextField, PasswordField, SubmitButton, FieldLabel, fieldClass } from '@components/auth/AuthFields'

const COUNTRY_CODES = [
  ['+225', '🇨🇮'], ['+226', '🇧🇫'], ['+227', '🇳🇪'], ['+228', '🇹🇬'], ['+229', '🇧🇯'],
  ['+223', '🇲🇱'], ['+224', '🇬🇳'], ['+221', '🇸🇳'], ['+240', '🇬🇶'],
]

// Robustesse indicative du mot de passe (0 à 4)
const passwordScore = (pwd) => {
  if (!pwd) return 0
  let score = 0
  if (pwd.length >= 8) score++
  if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) score++
  if (/\d/.test(pwd)) score++
  if (/[^A-Za-z0-9]/.test(pwd)) score++
  return score
}
const STRENGTH = [
  { label: 'Trop court', color: '#EF4444' },
  { label: 'Faible', color: '#EF4444' },
  { label: 'Moyen', color: '#F59E0B' },
  { label: 'Bon', color: '#22C55E' },
  { label: 'Excellent', color: '#059669' },
]

function RegisterPage() {
  const navigate = useNavigate()
  const { login } = useAuthStore()
  const [formData, setFormData] = useState({
    name: '',
    prenom: '',
    email: '',
    codeNumeroPays: '+225',
    numero: '',
    password: '',
    confirmPassword: '',
  })
  const [loading, setLoading] = useState(false)

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    // Validation
    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas')
      setLoading(false)
      return
    }

    try {
      const response = await authService.register({
        nom: formData.name,
        prenom: formData.prenom,
        email: formData.email,
        codeNumeroPays: formData.codeNumeroPays,
        numero: formData.numero,
        password: formData.password,
      })
      login(response.client, response.token, 'client')
      toast.success('Inscription réussie ! Bienvenue sur Kudhi')
      navigate(ROUTES.LIGDI)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de l\'inscription')
    } finally {
      setLoading(false)
    }
  }

  const tooShort = formData.password.length < 8
  const score = tooShort ? 0 : passwordScore(formData.password)
  const strength = STRENGTH[score]
  const confirmTouched = formData.confirmPassword.length > 0
  const passwordsMatch = formData.password === formData.confirmPassword

  return (
    <div className="animate-slide-up">
      <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#002A4E]">Créer votre compte</h2>
      <p className="mt-1 text-slate-500">Gratuit, en moins d&apos;une minute.</p>

      <form onSubmit={handleSubmit} className="mt-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <TextField
            label="Nom"
            icon="user"
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            autoComplete="family-name"
            placeholder="Votre nom"
          />
          <TextField
            label="Prénom"
            type="text"
            id="prenom"
            name="prenom"
            value={formData.prenom}
            onChange={handleChange}
            required
            autoComplete="given-name"
            placeholder="Votre prénom"
          />
        </div>

        <TextField
          label="Adresse email"
          icon="mail"
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          autoComplete="email"
          placeholder="vous@exemple.com"
        />

        <div>
          <FieldLabel htmlFor="numero">Numéro de téléphone</FieldLabel>
          <div className="flex gap-2">
            <select
              id="codeNumeroPays"
              name="codeNumeroPays"
              value={formData.codeNumeroPays}
              onChange={handleChange}
              aria-label="Indicatif pays"
              className={`${fieldClass} !w-32 flex-shrink-0 px-3 cursor-pointer`}
            >
              {COUNTRY_CODES.map(([code, flag]) => (
                <option key={code} value={code}>{flag} {code}</option>
              ))}
            </select>
            <input
              type="tel"
              id="numero"
              name="numero"
              value={formData.numero}
              onChange={handleChange}
              required
              autoComplete="tel-national"
              placeholder="XX XX XX XX XX"
              className={`${fieldClass} flex-1 min-w-0 px-4`}
            />
          </div>
        </div>

        <PasswordField
          label="Mot de passe"
          id="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          required
          minLength="8"
          autoComplete="new-password"
          placeholder="Minimum 8 caractères"
          hint={
            <div className="mt-2.5 flex items-center gap-3">
              <div className="flex flex-1 gap-1.5">
                {[1, 2, 3, 4].map((i) => (
                  <span
                    key={i}
                    className="h-1.5 flex-1 rounded-full transition-colors duration-300"
                    style={{ background: formData.password && i <= Math.max(score, 1) ? strength.color : '#E2E8F0' }}
                  />
                ))}
              </div>
              <span className="whitespace-nowrap text-right text-xs font-medium" style={{ color: formData.password ? strength.color : '#94A3B8' }}>
                {formData.password ? strength.label : '8 caractères min.'}
              </span>
            </div>
          }
        />

        <PasswordField
          label="Confirmer le mot de passe"
          id="confirmPassword"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleChange}
          required
          autoComplete="new-password"
          placeholder="Retapez votre mot de passe"
          hint={confirmTouched && (
            <p className={`mt-2 text-xs font-medium ${passwordsMatch ? 'text-emerald-600' : 'text-red-500'}`}>
              {passwordsMatch ? '✓ Les mots de passe correspondent' : 'Les mots de passe ne correspondent pas'}
            </p>
          )}
        />

        <SubmitButton loading={loading} loadingLabel="Inscription…">
          Créer mon compte
        </SubmitButton>

        <p className="text-center text-xs leading-relaxed text-slate-500">
          En créant un compte, vous acceptez nos{' '}
          <Link to="/terms" className="font-semibold text-[#002A4E] underline underline-offset-2">conditions d&apos;utilisation</Link>
          {' '}et notre{' '}
          <Link to="/privacy" className="font-semibold text-[#002A4E] underline underline-offset-2">politique de confidentialité</Link>.
        </p>
      </form>

      <p className="mt-5 text-center text-slate-600">
        Déjà un compte ?{' '}
        <Link to={ROUTES.LOGIN} className="font-bold text-[#B8860B] hover:underline underline-offset-2">
          Se connecter
        </Link>
      </p>
    </div>
  )
}

export default RegisterPage
